import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, LOGO } from "@/lib/api";
import { PasswordInput } from "@/components/PasswordInput";
import { useAuth } from "@/context/AuthContext";
import { Reveal } from "@/components/Reveal";
import { BackButton } from "@/components/BackButton";

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(searchParams.get("mode") === "register" ? "register" : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [agree, setAgree] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const next = searchParams.get("next");
  const isAdminFlow = next?.startsWith("/admin");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let user;
      if (mode === "login") {
        user = await login(form.email, form.password);
        toast.success("Welcome back");
      } else {
        if (!agree) {
          toast.error("Please accept the Terms & Conditions and Privacy Policy to create your account");
          setBusy(false);
          return;
        }
        user = await register(form.name, form.email, form.password);
        toast.success("Account created — welcome to BTMEDU");
      }
      if (next) navigate(next);
      else navigate(user.role === "admin" ? "/admin" : "/account");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full h-12 rounded-xl border border-slate-200 bg-white px-4 text-sm outline-none focus:ring-2 focus:ring-gold";

  // Dedicated ADMIN CONSOLE sign-in: full-screen navy, zero storefront chrome.
  // This page must be unmistakably the admin console — not the website.
  if (isAdminFlow) {
    return (
      <div className="min-h-screen bg-navy-dark flex flex-col items-center justify-center px-4 py-12" data-testid="admin-auth-page">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <span className="bg-white rounded-2xl px-4 py-3 inline-flex">
              <img src={LOGO} alt="BTMEDU" className="h-10 w-auto" />
            </span>
            <h1 className="font-display text-2xl font-bold text-white mt-6">Admin Console</h1>
            <p className="text-sm text-white/50 mt-1" data-testid="admin-signin-label">
              Staff access only — sign in to manage the store
            </p>
          </div>
          <div className="bg-white rounded-3xl p-8 shadow-2xl">
            <form onSubmit={submit} className="space-y-4">
              <input required type="email" placeholder="Admin email address" value={form.email} onChange={set("email")} className={inputCls} data-testid="auth-email" />
              <PasswordInput required minLength={6} placeholder="Password" value={form.password} onChange={set("password")} className={inputCls} data-testid="auth-password" />
              <button
                type="submit"
                disabled={busy}
                className="w-full h-12 rounded-full bg-gold hover:bg-gold-hover disabled:opacity-50 text-white font-bold text-sm transition-colors gold-glow"
                data-testid="auth-submit"
              >
                {busy ? "Please wait…" : "Sign in to admin console"}
              </button>
            </form>
            <div className="mt-4 text-center">
              {!forgotOpen ? (
                <button type="button" onClick={() => setForgotOpen(true)} className="text-xs font-semibold text-slate-400 hover:text-brand transition-colors" data-testid="forgot-password-link-admin">
                  Forgot your password?
                </button>
              ) : forgotSent ? (
                <p className="text-xs text-emerald-600 font-semibold bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3" data-testid="forgot-sent">
                  If that email has an account, a reset link is on its way — check your inbox (valid 1 hour).
                </p>
              ) : (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    setBusy(true);
                    try {
                      await api.post("/auth/forgot-password", { email: forgotEmail });
                      setForgotSent(true);
                    } catch (err) {
                      toast.error(errMsg(err));
                    } finally {
                      setBusy(false);
                    }
                  }}
                  className="flex gap-2"
                >
                  <input required type="email" placeholder="Your admin email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} className={inputCls + " h-11"} data-testid="forgot-email" />
                  <button type="submit" disabled={busy} className="h-11 px-4 rounded-xl bg-navy text-white text-xs font-bold whitespace-nowrap disabled:opacity-50" data-testid="forgot-submit">
                    {busy ? "Sending…" : "Email reset link"}
                  </button>
                </form>
              )}
            </div>
          </div>
          <p className="text-center mt-6">
            <Link to="/" className="text-xs font-semibold text-white/40 hover:text-white/80 transition-colors" data-testid="admin-auth-store-link">
              ← Go to the BTMEDU store website instead
            </Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-4 py-16" data-testid="auth-page">
      {next !== "/" && (
        <div className="mb-4">
          <BackButton to="/" label="Back to home" testId="auth-back-button" />
        </div>
      )}
      <Reveal>
        <div className="bg-white rounded-3xl border border-slate-200/80 p-8">
          <span className="bg-cream rounded-xl px-3 py-2 inline-flex mb-6">
            <img src={LOGO} alt="BTMEDU" className="h-8 w-auto" />
          </span>
          <div className="flex bg-cream rounded-full p-1 mb-7" data-testid="auth-tabs">
            {["login", "register"].map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={`flex-1 h-10 rounded-full text-sm font-bold transition-colors ${mode === m ? "bg-navy text-white" : "text-slate-500"}`}
                data-testid={`auth-tab-${m}`}
              >
                {m === "login" ? "Sign in" : "Create account"}
              </button>
            ))}
          </div>
          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <input required placeholder="Full name" value={form.name} onChange={set("name")} className={inputCls} data-testid="auth-name" />
            )}
            <input required type="email" placeholder="Email address" value={form.email} onChange={set("email")} className={inputCls} data-testid="auth-email" />
            <PasswordInput required minLength={6} placeholder="Password (min 6 characters)" value={form.password} onChange={set("password")} className={inputCls} data-testid="auth-password" />
            {mode === "register" && (
              <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="auth-consent-label">
                <input
                  type="checkbox"
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                  className="mt-0.5 w-[18px] h-[18px] rounded accent-[#E8722A] shrink-0 cursor-pointer"
                  data-testid="auth-consent-checkbox"
                />
                <span className="text-xs text-slate-500 leading-relaxed">
                  I have read and agree to the{" "}
                  <Link to="/terms" target="_blank" className="text-brand font-semibold hover:underline" data-testid="auth-terms-link">
                    Terms &amp; Conditions
                  </Link>{" "}
                  and the{" "}
                  <Link to="/privacy" target="_blank" className="text-brand font-semibold hover:underline" data-testid="auth-privacy-link">
                    Privacy Policy (POPIA)
                  </Link>
                  .
                </span>
              </label>
            )}
            <button
              type="submit"
              disabled={busy}
              className="w-full h-12 rounded-full bg-gold hover:bg-gold-hover disabled:opacity-50 text-white font-bold text-sm transition-colors gold-glow"
              data-testid="auth-submit"
            >
              {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
            </button>
          </form>
          {mode === "login" && (
            <div className="mt-4 text-center">
              {!forgotOpen ? (
                <button
                  type="button"
                  onClick={() => setForgotOpen(true)}
                  className="text-xs font-semibold text-slate-400 hover:text-brand transition-colors"
                  data-testid="forgot-password-link"
                >
                  Forgot your password?
                </button>
              ) : forgotSent ? (
                <p className="text-xs text-emerald-600 font-semibold bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3" data-testid="forgot-sent">
                  If that email has an account, a reset link is on its way — check your inbox (valid 1 hour).
                </p>
              ) : (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    setBusy(true);
                    try {
                      await api.post("/auth/forgot-password", { email: forgotEmail });
                      setForgotSent(true);
                    } catch (err) {
                      toast.error(errMsg(err));
                    } finally {
                      setBusy(false);
                    }
                  }}
                  className="flex gap-2"
                >
                  <input
                    required
                    type="email"
                    placeholder="Your email address"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    className={inputCls + " h-11"}
                    data-testid="forgot-email"
                  />
                  <button
                    type="submit"
                    disabled={busy}
                    className="h-11 px-4 rounded-xl bg-navy text-white text-xs font-bold whitespace-nowrap disabled:opacity-50"
                    data-testid="forgot-submit"
                  >
                    {busy ? "Sending…" : "Email reset link"}
                  </button>
                </form>
              )}
            </div>
          )}
          <p className="text-xs text-slate-500 mt-5 text-center">
            You can also check out as a guest — no account needed.
          </p>
          <div className="flex items-center gap-3 my-5">
            <span className="flex-1 h-px bg-slate-200" />
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">or</span>
            <span className="flex-1 h-px bg-slate-200" />
          </div>
          <button
            type="button"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + "/account";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full h-12 rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-navy font-bold text-sm flex items-center justify-center gap-2.5 transition-colors"
            data-testid="auth-google-btn"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
              <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C41 35.4 44 30.2 44 24c0-1.3-.1-2.6-.4-3.9z"/>
            </svg>
            Continue with Google
          </button>
          {next === "/" && (
            <button
              type="button"
              onClick={() => {
                sessionStorage.setItem("btm_guest_browse", "1");
                navigate("/");
              }}
              className="w-full mt-4 text-xs font-semibold text-slate-400 hover:text-navy transition-colors"
              data-testid="auth-guest-browse"
            >
              Continue browsing as guest →
            </button>
          )}
        </div>
      </Reveal>
    </div>
  );
}
